import Button from "components/form/Button";
import InputField from "components/form/InputField";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  decode,
  downloadPdfFromApi,
  encode,
  formatCurrency,
  validateEmail,
} from "utils/HelperFunctions";
import Drawer from "components/ui-component/SideDrawer";
import PaymentPreview from "components/payments/PaymentPreview";
import { SuccessPaymentIcon } from "icons/payments/successPayment";
import { useLeadData, usePortalVanityUrl } from "store/slices/dashboardSlice";
import { useAppDispatch } from "store/hooks";
import {
  paymentReceiptDownloadPdf,
  receiptIdGet,
} from "store/slices/paymentsSlice";
import { useMediaQuery } from "react-responsive";
import { useTestAttrsFactory } from "utils/testid";
import { useIsLogin } from "store/slices/commonSlice";
// import MessageToast from "components/ui-component/MessageToast";

const PaymentLinkProcessSuccess = ({
  email,
  amount,
  invoiceId,
  sendEmail,
  className,
  lead_id,
  openDrawer = false,
  closePreviewLocally = false,
}: any) => {
  const [emailInput, setEmailInput] = useState("");
  const [isErr, setIsErr] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined);
  const [id, setId] = useState<any>(invoiceId || "");
  const [isPreview, setIsPreview] = useState<boolean>(openDrawer);
  const [isEmailLoading, setIsEmailLoading] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState("");
  const navigate = useNavigate();
  const isLogin = useIsLogin();
  const { clusterId: cluster_id, id: actionId } = useParams();
  const accountLeadData = useLeadData();
  const vanityUrl = usePortalVanityUrl();
  const dispatch = useAppDispatch();
  const isLessThan590 = useMediaQuery({ query: "(max-width: 590px)" });
  const [receiptId, setReceiptId] = useState<any>(null);
  const testAttrs = useTestAttrsFactory();

  const handleSendEmail = async () => {
    setIsEmailLoading(true);
    if (!validateEmail(emailInput)) {
      setIsErr(true);
      setErrMsg("Invalid Email");
      setIsEmailLoading(false);
      return;
    }
    try {
      let emails = [emailInput];
      let value = {
        invoice_id: invoiceId,
        emails: emails,
        shouldSendAll: false,
      };
      await sendEmail(value);
      setEmailInput("");
    } catch (error) {
      console.error("Failed to send email:", error);
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleGetReceiptID = async (params: any) => {
    try {
      const response: any = await dispatch(receiptIdGet(params)).unwrap();
      if (response?.body?.success) {
        setReceiptId(response.body.data?.id);
        return response.body.data?.id;
      }
      return params?.orders_id;
    } catch (error) {
      console.error("Failed to get receipt ID:", error);
    }
  };

  const handleDownload = async () => {
    let receipt_id = receiptId;
    if (!receipt_id) {
      receipt_id = await handleGetReceiptID({
        orders_id: invoiceId,
      });
    }

    await handleDownloadPDF(invoiceId, receipt_id);
  };

  const handleDownloadPDF = async (id: any, receiptId?: any) => {
    setIsDownloadLoading("true");
    let slug = decode(actionId || "")?.split("_");
    let pid = encode(`${id}_${slug[1]}_${new Date().getTime()}`, "pid_");
    try {
      let payload = {
        invoice_id: id,
        lead_id: accountLeadData?.lead_id,
        url: `${vanityUrl}view-payment/${pid}`,
        cluster_id: slug[1],
        isFromPortal: true,
      };

      await downloadPdfFromApi(
        () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
        `Receipt - ${receiptId || id}.pdf`,
        (loading) => setIsDownloadLoading(loading ? id : "")
      );
    } catch (error) {
      console.error("Failed to download PDF:", error);
    } finally {
      setIsDownloadLoading("");
    }
  };

  return (
    <div
      className={`w-full h-full flex items-center text-center max-w-[630px] ${className}`}
      {...testAttrs("process-success", "Payment link payment completed")}
    >
      <div className="h-full flex flex-col justify-center items-center gap-24 ">
        <div className="w-full flex flex-col justify-center items-center gap-10">
          <SuccessPaymentIcon aria-hidden="true" />
          <div className="text-txtPrimary lg:text-2xl leading-8 text-xl font-medium">
            Payment successfully completed
          </div>
          <div className="w-full flex flex-col justify-center items-center">
            <div className="w-full text-center bg-bgThird lg:py-5 py-4 lg:px-2 px-3 lg:text-lg text-base text-txtParagraph break-words mb-4 font-normal">
              Your payment of{" "}
              <span className="font-bold">
                {formatCurrency(amount) || "$0.00"}
              </span>{" "}
              has been received.
            </div>
            <div className="lg:text-base text-sm text-txtParagraph">
              <span
                className={`text-btnPrimaryActive ${invoiceId ? "hover:!text-btnPrimaryHover cursor-pointer" : "cursor-not-allowed"}`}
                role="button"
                tabIndex={0}
                {...testAttrs("view-receipt", "View payment receipt")}
                onClick={() => {
                  if (!invoiceId) return;
                  setId(Number(invoiceId));
                  setIsPreview(true);
                }}
              >
                Click here
              </span>{" "}
              to view your payment receipt.
            </div>
          </div>
        </div>
        <div>
          <div className="flex-col flex justify-center items-center gap-[30px]">
            <div className="text-txtParagraph lg:text-base text-sm whitespace-normal break-words text-center">
              A copy of this payment receipt will automatically be sent to{" "}
              <span className="font-bold">{email}</span>. To send a copy to an
              additional email, enter the address below.
            </div>
            <div className="px-[10px] w-full">
              <InputField
                type="email"
                name="email"
                label=""
                placeholder="Enter your email address"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setIsErr(false);
                  setErrMsg(undefined);
                }}
                className="!py-3"
                error={errMsg}
                isErr={isErr}
                {...testAttrs(
                  "receipt-email",
                  "Additional email for payment receipt"
                )}
              />
            </div>
            <div className="m-[10px] w-full flex justify-center items-center gap-5">
              <Button
                text="Send Email"
                className="w-full"
                isLoading={isEmailLoading}
                isDisabled={emailInput.length === 0 /*&& !isEmailSent*/}
                {...testAttrs("send-receipt-email", "Send receipt email")}
                // isSuccess={isEmailSent}
                onClick={() => {
                  if (emailInput.length === 0) {
                    return;
                  }
                  handleSendEmail();
                }}
              />
              <Button
                text="Download"
                className="w-full"
                isLoading={isDownloadLoading !== "" ? true : false}
                {...testAttrs("download-receipt", "Download receipt")}
                onClick={handleDownload}
                isDisabled={invoiceId ? false : true}
                // isSuccess={isRecieptDownloaded}
              />
            </div>
          </div>
          <div className="w-full lg:pb-0 pb-5 lg:text-base text-sm lg:pt-6 pt-5 text-center text-btnPrimaryActive hover:!text-btnPrimaryHover ">
            <div
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              {...testAttrs("back-to-dashboard", "Back to dashboard")}
              onClick={() => {
                let slug = decode(actionId || "")?.split("_");
                let clusterId = cluster_id
                  ? cluster_id
                  : encode(`${slug[1]}`, "ci_");

                navigate(`/${clusterId}/dashboard`);
              }}
            >
              Back to dashboard
            </div>
          </div>
        </div>
      </div>
      <Drawer
        direction="right"
        open={isPreview}
        size={isLessThan590 ? `100vw` : "590px"}
        onClose={() => {
          setIsPreview(false);
          setId("");
        }}
        overlayColor="#000"
        lockBackgroundScroll={true}
        enableOverlay={true}
      >
        <div className="contents" {...testAttrs("receipt-drawer")}>
          <PaymentPreview
            id={id}
            setId={setId}
            show={isPreview}
            setShow={() => {
              if (openDrawer && !closePreviewLocally) {
                const decoded = decode(actionId || "")?.split("_");
                const clusterId = cluster_id
                  ? cluster_id
                  : encode(`${decoded[1]}`, "ci_");

                if (isLogin) {
                  navigate(`/${clusterId}/payments`, { replace: true });
                } else {
                  navigate(`/${clusterId}/login`, { replace: true });
                }
                return;
              }

              setIsPreview(false);
            }}
            onDownloadCLick={(id: any, receiptId: any) =>
              handleDownloadPDF(id, receiptId)
            }
            isDownloading={isDownloadLoading.toString()}
          />
        </div>
      </Drawer>
    </div>
  );
};

export default PaymentLinkProcessSuccess;
